import React, { FC, useState } from 'react'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import { Container, ContainerVersion, Video } from '../../types'
import { getVideo, createNewVersion, discardDraft, ContainerResolution } from '../../system/apitools'
import { getShortID } from '../../system/id'
import SectionWrapper from './LockedSectionWrapper'
import useRouter from '../../hooks/useRouter'
import useSnackbar from '../../hooks/useSnackbar'

interface VersionsSectionProps {
  container: Container
  // The video currently open in the admin page — "Publish new version" cuts
  // its next version FROM this one (POST /videos/:id/new-version names a
  // source video, not a container), so this can be any version's video, not
  // necessarily the latest.
  sourceVideoId: string
  onContainerChange: (updated: Container) => void
}

const formatDate = (iso?: string): string => {
  if (!iso) return 'unknown time'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'unknown time'
  return d.toLocaleString()
}

const lastNonDeletedIndex = (versions: ContainerVersion[]): number => {
  for (let i = versions.length - 1; i >= 0; i--) {
    if (!versions[i].deleted_at) return versions[i].index
  }
  return -1
}

const VersionRow: FC<{
  version: ContainerVersion
  isCurrent: boolean
  video: Video | undefined
  videoLoading: boolean
  onOpen: () => void
}> = ({ version, isCurrent, video, videoLoading, onOpen }) => {
  const deleted = !!version.deleted_at
  const rootHash = video?.publication?.root_hash

  return (
    <Box
      onClick={deleted ? undefined : onOpen}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: deleted ? 'default' : 'pointer',
        opacity: deleted ? 0.6 : 1,
        '&:hover': deleted ? {} : { bgcolor: 'action.hover' },
      }}
    >
      <Chip label={`v${version.index + 1}`} size="small" color={isCurrent ? 'primary' : 'default'} />
      {isCurrent && <Chip label="Current" size="small" color="success" variant="outlined" />}
      {deleted && <Chip label="Deleted" size="small" color="error" variant="outlined" />}
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" sx={{ textDecoration: deleted ? 'line-through' : 'none' }}>
          {getShortID(version.video_id)} · published {formatDate(version.added_at)}
          {deleted && ` · deleted ${formatDate(version.deleted_at)}`}
        </Typography>
        {!deleted && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: rootHash ? 'monospace' : undefined, wordBreak: 'break-all' }}
          >
            {videoLoading
              ? 'Loading root hash…'
              : rootHash
                ? `Root hash: ${rootHash}`
                : 'Root hash unavailable'}
          </Typography>
        )}
      </Box>
      <Chip
        label={version.chain ? 'Anchored on chain' : 'Not anchored'}
        size="small"
        color={version.chain ? 'success' : 'default'}
        variant={version.chain ? 'filled' : 'outlined'}
      />
    </Box>
  )
}

// VersionsSection is the container's publication history: every version
// still in the list, in order, plus the single in-flight draft (if any).
// Nothing here is ever reordered or edited. A deleted version's fate turns on
// whether THAT ENTRY was ever recorded as appended on chain
// (ContainerVersion.Chain != nil, i.e. a VersionRef was reported back for
// it) — not on whether the container as a whole is anchored. A claimed entry
// stays put with DeletedAt stamped, because an index the chain has assigned
// never moves (see types.ContainerVersion's doc comment); an unclaimed entry
// is removed and the survivors renumbered instead, which covers every
// unclaimed entry of an unanchored container AND the unclaimed, unsealed tail
// of an anchored one (see data.planVersionDeletion) — so it simply is not
// here to render.
const VersionsSection: FC<VersionsSectionProps> = ({ container, sourceVideoId, onContainerChange }) => {
  const router = useRouter()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)

  const nonDeletedVersions = container.versions.filter(v => !v.deleted_at)
  const videoQueries = useQueries({
    queries: nonDeletedVersions.map(v => ({
      queryKey: ['video', v.video_id],
      queryFn: () => getVideo(v.video_id),
    })),
  })
  const videoByID = new Map<string, Video>()
  const loadingByID = new Map<string, boolean>()
  nonDeletedVersions.forEach((v, i) => {
    if (videoQueries[i].data) videoByID.set(v.video_id, videoQueries[i].data as Video)
    loadingByID.set(v.video_id, videoQueries[i].isPending)
  })

  const currentIndex = lastNonDeletedIndex(container.versions)
  const latestVersion = container.versions.length > 0 ? container.versions[container.versions.length - 1] : undefined

  const hasDraft = !!container.draft_video_id
  // Mirrors data.guardDraftable exactly: a container that was never anchored
  // has nothing on chain to diverge from, so it is unaffected.
  const latestUnanchored = !!container.chain && !!latestVersion && !latestVersion.chain
  const disabledReason = hasDraft
    ? 'A draft version already exists — continue editing it or discard it below.'
    : latestUnanchored
      ? 'The latest version is not anchored on chain yet. Anchor it (see Anchor on Solana, above) before cutting a new version.'
      : null

  const newVersionMutation = useMutation({
    mutationFn: () => createNewVersion(sourceVideoId),
    onSuccess: ({ video, container: updated }) => {
      onContainerChange(updated)
      queryClient.setQueryData(['video', video.id], video)
      // Primes the query the draft's own admin page will make on mount, so
      // ContainerSection/VersionsSection render there immediately instead of
      // flashing empty until resolveContainerForVideo comes back.
      const primed: ContainerResolution = {
        container: updated,
        resolved_kind: 'video_id',
        canonical_slug: updated.slug,
        video,
      }
      queryClient.setQueryData(['container-for-video', video.id], primed)
      snackbar.success('Draft version created')
      router.navigate('admin-video', { id: video.id })
    },
    onError: (err: any) => snackbar.error(`Failed to create new version: ${err?.response?.data?.error || err.message}`),
  })

  const discardMutation = useMutation({
    mutationFn: () => discardDraft(container.id),
    onSuccess: (updated) => {
      onContainerChange(updated)
      snackbar.success('Draft discarded')
    },
    onError: (err: any) => snackbar.error(`Failed to discard draft: ${err?.response?.data?.error || err.message}`),
  })

  return (
    <SectionWrapper title="Versions" status="pending">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every version ever published under this container, oldest first. Each is a complete,
        independent copy with its own root hash and on-chain account — publishing a new one never
        changes any of the ones before it.
      </Typography>

      {container.versions.map(v => (
        <VersionRow
          key={v.video_id}
          version={v}
          isCurrent={v.index === currentIndex}
          video={videoByID.get(v.video_id)}
          videoLoading={!!loadingByID.get(v.video_id)}
          onOpen={() => router.navigate('admin-video', { id: v.video_id })}
        />
      ))}

      {hasDraft && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.5,
            px: 1,
            mt: 1,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Chip label="Draft" size="small" color="info" />
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            An unpublished next version is being prepared — {getShortID(container.draft_video_id!)}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => router.navigate('admin-video', { id: container.draft_video_id })}
          >
            Continue
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => setConfirmDiscardOpen(true)}
            disabled={discardMutation.isPending}
          >
            {discardMutation.isPending ? 'Discarding...' : 'Discard'}
          </Button>
        </Box>
      )}

      {disabledReason && (
        <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
          {disabledReason}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          onClick={() => newVersionMutation.mutate()}
          disabled={!!disabledReason || newVersionMutation.isPending}
        >
          {newVersionMutation.isPending ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} /> Creating...
            </>
          ) : (
            'Publish new version'
          )}
        </Button>
      </Box>

      <Dialog open={confirmDiscardOpen} onClose={() => setConfirmDiscardOpen(false)}>
        <DialogTitle>Discard this draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes the draft version's copied content — its title, description,
            subtitles and any edits made to it since it was cut. The published versions before it
            are completely unaffected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscardOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmDiscardOpen(false)
              discardMutation.mutate()
            }}
          >
            Discard draft
          </Button>
        </DialogActions>
      </Dialog>
    </SectionWrapper>
  )
}

export default VersionsSection
